import { useMemo } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { LegacyDynamicFormTable } from '~/legacy-components/LegacyDynamicFormTable';
import { LegacyFAIcon, LegacyFAIcons } from '~/legacy-components/LegacyFAIcon';
import { LegacyHeadingWithActions } from '~/legacy-components/LegacyLayout';

import { ActionButtons } from './ActionButtons';
import { mappedData, readData } from './data/dataMapping';
import { getCellConfig } from './data/getCellConfig';
import { useEquipmentTab } from './useEquipmentTab';

export const EquipmentTab = () => {
  const { t } = useTranslation();

  const { selectedEmployeeId } = useSelectedEmployeeId();
  const empId = String(selectedEmployeeId);
  const myRoles = useLoggedInUserRoles();

  const {
    companyEquip,
    equipEnumType,
    onSubmit,
    useFieldArrayMethods,
    updateInvNumber,
    invNumber,
    equipList,
    useFormMethods,
    defaultData,
    setEditedRows,
    setDeletedRows,
    editMode,
    setEditMode,
  } = useEquipmentTab();

  const headers = useMemo(() => {
    return [
      { title: t('laType'), width: '150px', required: true },
      { title: t('laEquipmentItem'), width: '170px' },
      { title: t('laEquipmentNumber'), width: '125px', required: true },
      { title: t('laDeliveryDate'), width: '105px', required: true },
      { title: t('laReturnedDate'), width: '105px' },
      { title: t('laEquipmentInfo'), width: '148px' },
      { title: t('laAttachment'), width: '170px' },
      { title: t('laExpires'), width: '100px' },
      { title: '', width: '20px' },
    ];
  }, [t]);

  const getCellConfigFunction = useMemo(() => {
    return getCellConfig(
      equipEnumType,
      equipList,
      setEditedRows,
      useFormMethods,
      myRoles,
    );
  }, [equipEnumType, equipList, myRoles, setEditedRows, useFormMethods]);

  const memoMappedData = useMemo(() => {
    if (editMode) {
      return mappedData(
        useFieldArrayMethods,
        useFormMethods,
        equipList,
        setDeletedRows,
        getCellConfigFunction,
      );
    }
    return { editData: [] };
  }, [
    editMode,
    equipList,
    getCellConfigFunction,
    setDeletedRows,
    useFieldArrayMethods,
    useFormMethods,
  ]);

  const mappedReadData = useMemo(() => {
    return readData(companyEquip, empId, equipEnumType);
  }, [companyEquip, empId, equipEnumType]);

  const FormTableHeader = useMemo(() => {
    return (
      <LegacyHeadingWithActions
        headingIcon={<LegacyFAIcon FAIcon={LegacyFAIcons.Table} />}
        heading={`${t('laCompanyEquipment')}`}
      >
        <ActionButtons
          setEditMode={setEditMode}
          setEditedRows={setEditedRows}
          setDeletedRows={setDeletedRows}
          useFormMethods={useFormMethods}
          onSubmit={onSubmit}
          defaultData={defaultData}
          useFieldArrayMethods={useFieldArrayMethods}
          invNumber={invNumber}
          updateInvNumber={updateInvNumber}
          equipList={equipList}
          editMode={editMode}
        />
      </LegacyHeadingWithActions>
    );
  }, [
    defaultData,
    editMode,
    equipList,
    invNumber,
    onSubmit,
    setDeletedRows,
    setEditMode,
    setEditedRows,
    updateInvNumber,
    t,
    useFieldArrayMethods,
    useFormMethods,
  ]);
  const TableComponent = useMemo(() => {
    return (
      <LegacyDynamicFormTable
        control={useFormMethods.control as unknown as Control}
        formTableHeader={FormTableHeader}
        headers={headers}
        readData={mappedReadData}
        editData={memoMappedData?.editData}
        editMode={editMode}
      />
    );
  }, [
    FormTableHeader,
    editMode,
    headers,
    mappedReadData,
    memoMappedData,
    useFormMethods.control,
  ]);

  return <>{TableComponent}</>;
};
